"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { CareDock } from "@/components/feedback/CareDock";
import { useAnimatedOpen } from "@/components/ui/use-animated-open";
import { FirstSuccessPrompt } from "@/components/feedback/FirstSuccessPrompt";
import { UsefulnessPrompt } from "@/components/feedback/UsefulnessPrompt";
import { CARE_DOCK_SUCCESS_EVENT } from "@/lib/care-dock-keys";
import { dismissCareDock, markCareDockCta } from "@/lib/care-dock-storage";
import { CARE_TOOL_SUCCESS_EVENT, type CareTool } from "@/lib/care-prompt-keys";
import { completeFirstSuccess, dismissUsefulness, markUsefulnessVote, resolveCareSurfaceAfterSuccess } from "@/lib/care-prompt-storage";
import { submitCareFeedback } from "@/lib/submit-care-feedback";
type ActivePrompt = {
  surface: "first-success";
  tool: CareTool;
} | {
  surface: "usefulness";
  tool: CareTool;
} | {
  surface: "care-dock";
  tool: CareTool;
} | null;

/**
 * Global care queue host: first-success → usefulness → Care Dock (one at a time).
 * Listens for CARE_TOOL_SUCCESS_EVENT and legacy CARE_DOCK_SUCCESS_EVENT.
 */
export function CareDockHost() {
  const $ = _c(33);
  const [active, setActive] = useState(null);
  const activeRef = useRef(null);
  const usefulnessVotedRef = useRef(false);
  const {
    shouldRender,
    isLeaving
  } = useAnimatedOpen(active !== null, 150);
  const [lastShown, setLastShown] = useState(null);
  if (active !== null && active !== lastShown) {
    setLastShown(active);
  }
  let t0;
  let t1;
  if ($[0] !== active) {
    t0 = () => {
      activeRef.current = active;
      if (active?.surface === "usefulness") {
        usefulnessVotedRef.current = false;
      }
    };
    t1 = [active];
    $[0] = active;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = tool => {
      if (activeRef.current) {
        return;
      }
      const surface = resolveCareSurfaceAfterSuccess(tool);
      if (!surface) {
        return;
      }
      if (surface === "first-success") {
        setActive({
          surface: "first-success",
          tool
        });
        return;
      }
      if (surface === "usefulness") {
        setActive({
          surface: "usefulness",
          tool
        });
        return;
      }
      setActive({
        surface: "care-dock",
        tool
      });
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleToolSuccess = t2;
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const onTool = ev => {
        const detail = (ev as CustomEvent<{
          tool?: CareTool;
        }>).detail;
        const tool_0 = detail?.tool;
        if (tool_0 === "chat") {
          handleToolSuccess(tool_0);
        }
      };
      const onLegacy = () => {
        handleToolSuccess("chat");
      };
      window.addEventListener(CARE_TOOL_SUCCESS_EVENT, onTool);
      window.addEventListener(CARE_DOCK_SUCCESS_EVENT, onLegacy);
      return () => {
        window.removeEventListener(CARE_TOOL_SUCCESS_EVENT, onTool);
        window.removeEventListener(CARE_DOCK_SUCCESS_EVENT, onLegacy);
      };
    };
    t4 = [handleToolSuccess];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== active) {
    t5 = () => {
      if (!active) {
        return;
      }
      const onKey = e => {
        if (e.key !== "Escape") {
          return;
        }
        e.preventDefault();
        if (active.surface === "care-dock") {
          dismissCareDock();
          submitCareFeedback({
            source: "care_dock",
            action: "dismiss",
            tool: active.tool
          });
        } else {
          if (active.surface === "usefulness") {
            dismissUsefulness();
            if (!usefulnessVotedRef.current) {
              submitCareFeedback({
                source: "usefulness",
                action: "dismiss",
                tool: active.tool
              });
            }
          } else {
            completeFirstSuccess();
            submitCareFeedback({
              source: "first_success",
              action: "dismiss",
              tool: active.tool
            });
          }
        }
        setActive(null);
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    };
    t6 = [active];
    $[6] = active;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  const shown = active ?? lastShown;
  if (!shouldRender || !shown) {
    return null;
  }
  if (shown.surface === "first-success") {
    let t7;
    let t8;
    if ($[9] !== shown.tool) {
      t7 = () => {
        completeFirstSuccess();
        submitCareFeedback({
          source: "first_success",
          action: "dismiss",
          tool: shown.tool
        });
        setActive(null);
      };
      t8 = () => {
        completeFirstSuccess();
        submitCareFeedback({
          source: "first_success",
          action: "cta",
          tool: shown.tool
        });
        setActive(null);
      };
      $[9] = shown.tool;
      $[10] = t7;
      $[11] = t8;
    } else {
      t7 = $[10];
      t8 = $[11];
    }
    let t9;
    if ($[12] !== isLeaving || $[13] !== shown.tool || $[14] !== t7 || $[15] !== t8) {
      t9 = <FirstSuccessPrompt tool={shown.tool} leaving={isLeaving} onDismiss={t7} onCta={t8} />;
      $[12] = isLeaving;
      $[13] = shown.tool;
      $[14] = t7;
      $[15] = t8;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    return t9;
  }
  if (shown.surface === "usefulness") {
    let t7;
    let t8;
    let t9;
    if ($[17] !== shown.tool) {
      t7 = () => {
        dismissUsefulness();
        if (!usefulnessVotedRef.current) {
          submitCareFeedback({
            source: "usefulness",
            action: "dismiss",
            tool: shown.tool
          });
        }
        setActive(null);
      };
      t8 = () => {
        usefulnessVotedRef.current = true;
        markUsefulnessVote();
        submitCareFeedback({
          source: "usefulness",
          action: "yes",
          tool: shown.tool
        });
        window.setTimeout(() => setActive(null), 1800);
      };
      t9 = payload => {
        usefulnessVotedRef.current = true;
        markUsefulnessVote();
        submitCareFeedback({
          source: "usefulness",
          action: "no",
          tool: shown.tool,
          message: payload?.message ?? null,
          reasonCode: payload?.reasonCode ?? null
        });
        window.setTimeout(() => setActive(null), 2800);
      };
      $[17] = shown.tool;
      $[18] = t7;
      $[19] = t8;
      $[20] = t9;
    } else {
      t7 = $[18];
      t8 = $[19];
      t9 = $[20];
    }
    let t10;
    if ($[21] !== isLeaving || $[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
      t10 = <UsefulnessPrompt leaving={isLeaving} onDismiss={t7} onPositive={t8} onNegative={t9} />;
      $[21] = isLeaving;
      $[22] = t7;
      $[23] = t8;
      $[24] = t9;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    return t10;
  }
  let t7;
  let t8;
  if ($[26] !== shown.tool) {
    t7 = () => {
      dismissCareDock();
      submitCareFeedback({
        source: "care_dock",
        action: "dismiss",
        tool: shown.tool
      });
      setActive(null);
    };
    t8 = kind => {
      markCareDockCta();
      submitCareFeedback({
        source: "care_dock",
        action: kind === "error" ? "report" : "suggest",
        tool: shown.tool
      });
      setActive(null);
    };
    $[26] = shown.tool;
    $[27] = t7;
    $[28] = t8;
  } else {
    t7 = $[27];
    t8 = $[28];
  }
  let t9;
  if ($[29] !== isLeaving || $[30] !== t7 || $[31] !== t8) {
    t9 = <CareDock leaving={isLeaving} onDismiss={t7} onCta={t8} />;
    $[29] = isLeaving;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
}